import React, { useCallback, useEffect, useMemo, useState } from "react";
import { endOfDay, startOfDay } from "date-fns";
import { useLazyQuery, useQuery } from "@apollo/client";
import { useToast } from "@npm-telluria-tecnologia/telluria-ui";

// region Hooks
import { useWindowDimensions } from "@hooks/useWindowDimensions";
import { useAuth } from "@hooks/useAuth";
// endregion Hooks
// region Atoms
import { useSelectClient } from "@atoms/SelectClient";
import { LastConcreting, Loading } from "@atoms/index";
// endregion Atoms
// region Graphql
import { travelAllWithPagination, TTravelAllWithPaginationQueries } from "@graphql/groups/travel/query.graphql";
import {
  ECommandResultStatus,
  ETravelStatus,
  EWhereClausesOperators,
  PermissionUserQueriesGetAllWithoutPaginationArgs,
  TravelQueriesGetAllWithPaginationArgs,
  TravelType
} from "@store/graphql/helpers/types.graphql";
import {
  permissionUserAllWithoutPagination,
  TPermissionAllWithoutPaginationQueries
} from "@graphql/groups/permissionUser/query.graphql";
// endregion Graphql
// region Store
import { ModulesID } from "@store/constants";
// endregion Store
// region Helpers
import utils from "@helpers/utils";
// endregion Helpers
// region Styles
import * as Styled from "./styles";
// endregion Styles

const WidgetsLastConcretings: React.FC = () => {
  const [whereConcretingIds, setWhereConcretingIds] = useState<string>("");
  const [travels, setTravels] = useState<TravelType[]>([]);
  const { width } = useWindowDimensions();
  const [travelsQuantityToShow, setTravelsQuantityToShow] = useState(3);

  const { value: selectedClient } = useSelectClient();

  const { addToast } = useToast();
  const { getDecodedToken } = useAuth();
  const token = getDecodedToken();

  const {
    loading: loadingPermissions,
    error: errorPermissions,
    data: dataPermissions
  } = useQuery<TPermissionAllWithoutPaginationQueries,
  PermissionUserQueriesGetAllWithoutPaginationArgs>(permissionUserAllWithoutPagination(["contract.concretings.id"]), {
    fetchPolicy: "network-only",
    pollInterval: 60000,
    variables: {
      where: [
        {
          property: "user.id",
          operator: EWhereClausesOperators.EQUALS,
          value: token.primarysid
        },
        {
          property: "construction.client.id",
          operator: EWhereClausesOperators.EQUALS,
          value: selectedClient.id
        },
        {
          property: "module.id",
          operator: EWhereClausesOperators.EQUALS,
          value: ModulesID.CONCRETING
        }
      ]
    }
  });

  const [
    getTravels,
    {
      loading: loadingTravels,
      error: errorTravels,
      data: dataTravels
    }
  ] = useLazyQuery<TTravelAllWithPaginationQueries,
    TravelQueriesGetAllWithPaginationArgs>(
      travelAllWithPagination([
        "id",
        "status",
        "invoiceIssueTime",
        "scheduledTime",
        "plantDepartureTime",
        "dischargeStartTime",
        "dischargeEndTime",
        "constructionStartTime",
        "constructionEndTime",
        "finishTime",
        "documentNumber",
        "concreteMixer.code",
        "concreting.id"
      ]),
      {
        fetchPolicy: "network-only",
        pollInterval: 60000
      }
    );

  const filters = useMemo(() => {

    const defaultFilter = [
      {
        property: "concreting.id",
        operator: EWhereClausesOperators.IN,
        value: whereConcretingIds
      },
      {
        property: "status",
        operator: EWhereClausesOperators.IN,
        value: `${ETravelStatus.CANCELED}|${ETravelStatus.DELIVERED}|${ETravelStatus.DISCHARGING}|
                ${ETravelStatus.INVOICE_ISSUED}|${ETravelStatus.IN_CONSTRUCTION}|${ETravelStatus.IN_TRANSIT}|
                ${ETravelStatus.PREPARING}`
      }
    ];

    const today = new Date();
    const startDay = startOfDay(today)
      .toISOString();
    const endDay = endOfDay(today)
      .toISOString();

    return [
      ...defaultFilter,
      {
        property: "concreting.scheduledDate",
        operator: EWhereClausesOperators.GREATER_THAN_OR_EQUAL,
        value: startDay
      },
      {
        property: "concreting.scheduledDate",
        operator: EWhereClausesOperators.LESS_THAN_OR_EQUAL,
        value: endDay
      }
    ];
  }, [whereConcretingIds]);

  const makeWhereConcretingIds = useCallback(() => {
    const response = dataPermissions?.permissionUser.getAllWithoutPagination;

    if (response?.status === ECommandResultStatus.SUCCESS) {
      const concretingIds: string[] = [];

      response.result?.forEach((permission) => {
        permission?.contract?.concretings?.forEach((concreting) => {
          concretingIds.push(concreting?.id || "");
        });
      });

      const concretingIdsStr = concretingIds
        .filter((id) => id && id !== "")
        .join("|");

      if (!concretingIdsStr || concretingIdsStr === "") {
        setWhereConcretingIds("");

        return;
      }

      setWhereConcretingIds(concretingIdsStr);
    }
  }, [dataPermissions]);

  const fillWidgetTravels = useCallback(
    (statusTravelArray: TravelType[], newTravels: TravelType[]) => {

      if (newTravels.length === travelsQuantityToShow || statusTravelArray.length === 0) {
        return newTravels;
      }

      const travelToAdd = statusTravelArray.at(-1);

      // eslint-disable-next-line no-param-reassign
      newTravels = [...newTravels, travelToAdd] as TravelType[];

      const newStatusTravelArray = statusTravelArray.filter(
        (travel) => travel.id !== travelToAdd?.id
      );

      if (
        newTravels.length < travelsQuantityToShow
        && newStatusTravelArray.length > 0
      ) {
        return fillWidgetTravels(newStatusTravelArray, newTravels);
      }

      return newTravels;
    },
    [travelsQuantityToShow]
  );

  const loadTravels = useCallback(async () => {
    if (whereConcretingIds === "") return;

    const response = dataTravels?.travel.getAllWithPagination;

    if (response?.status === ECommandResultStatus.SUCCESS) {
      const widgetTravels = utils.groupAndSortTravels(response?.result ?? []);

      setTravels(widgetTravels.slice(0, travelsQuantityToShow));
    }
  }, [
    dataTravels?.travel.getAllWithPagination,
    travelsQuantityToShow,
    whereConcretingIds
  ]);

  useEffect(() => {
    if (dataPermissions) makeWhereConcretingIds();
    if (errorPermissions) {
      addToast({
        type: "error",
        title: "Internal client error",
        description: errorPermissions.message
      });
    }
  }, [dataPermissions, errorPermissions, addToast, makeWhereConcretingIds]);

  useEffect(() => {
    if (whereConcretingIds !== "") {
      getTravels({
        variables: { where: filters }
      });
    }
  }, [getTravels, whereConcretingIds, filters]);

  useEffect(() => {
    if (dataTravels) loadTravels();
    if (errorTravels) {
      addToast({
        type: "error",
        title: "Internal client error",
        description: errorTravels.message
      });
    }
  }, [addToast, dataTravels, errorTravels, loadTravels]);

  useEffect(() => {
    setTravels([]);
  }, [selectedClient]);

  useEffect(() => {
    if (width >= 900) {
      setTravelsQuantityToShow(7);

      return;
    }

    setTravelsQuantityToShow(3);
  }, [width]);

  return (
    <Styled.Container>
      <Styled.Content>
        {(loadingPermissions || loadingTravels) && <Loading />}
        {!loadingPermissions
          && !loadingTravels
          && travels.length > 0
          && travels.map((travel) => <LastConcreting key={travel.id} travel={travel} />)}
        {!loadingPermissions && !loadingTravels && travels.length === 0 && (
          <Styled.WithoutTravelContent>
            Não há programações para hoje!
          </Styled.WithoutTravelContent>
        )}
      </Styled.Content>

      <Styled.Title>Programações do dia</Styled.Title>
    </Styled.Container>
  );
};

export default WidgetsLastConcretings;
